import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
    ReportLauncherCard,
    ReportModal,
    SummaryCard,
    ExportBar,
    PeriodFilter,
    ConsumptionTrendChart,
    emptyPeriod,
    periodToQueryParams,
    formatCurrency,
} from './ReportsShared';
import { getItem } from '../../../../utils/localStorage';
import { makeRequest2 } from '../../../../utils/makeRequest';
import { toastify } from '../../../../utils/toast';
import {
    GET_POWER_MONEY_COLLECTED_REPORT_URL,
    GET_FACILITY_INVOICES_URL,
    GET_POWER_MONTHLY_USAGE_URL,
    GET_FACILITY_CONSUMPTION_TREND_URL,
    NORTHLANDS_POWER_TRANSACTIONS_URL,
    NORTHLANDS_POWER_TRANSACTIONS_ENABLED_URL,
    GET_FACILITY_POWER_REPORT_URL, // NEW — add this constant in utils/urls.js, pointing at
                                    // `${powerReportsBaseRoute}/facility_power_report` on the gateway
} from '../../../../utils/urls';

// ─────────────────────────────────────────────────────────────
// Wired to real endpoints where they exist:
//   - Money Collected  -> new /reports/money_collected_report (built this sprint)
//   - Invoices          -> existing get_facility_invoices — response
//                          shape below is a best guess, see fetchInvoices
//   - Meter Consumption -> existing power_usage_monthly_graph (single
//                          month only — no customer/unit/reading data
//                          yet, see note in that modal)
//   - Trend + Facility   -> new /reports/facility_consumption_trend,
//     Consumption           one aggregation over PowerMeterMonthlyReading
//                           for the last 12 months. Both reports share
//                           this single fetch (table view vs chart view)
//   - Facility Power     -> new /reports/facility_power_report — per-category
//     Report (Residential/Commercial/Common Area) consumption, cost and
//     collected for a single chosen month, via the Power Meter Service gateway
// ─────────────────────────────────────────────────────────────

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function PowerReportsTab() {
    const [activeModal, setActiveModal] = useState(null);
    const closeModal = () => setActiveModal(null);

    // ── Report 1: Meter Consumption Per Period ──────────────────────────
    const [meterPeriodMonth, setMeterPeriodMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
    const [meterSearch, setMeterSearch] = useState('');
    const [meterLoading, setMeterLoading] = useState(false);
    const [meterGenerated, setMeterGenerated] = useState(false);
    const [meterRows, setMeterRows] = useState([]);

    const fetchMeterConsumption = async () => {
        try {
            setMeterLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const [year, month] = meterPeriodMonth.split('-');

            const response = await makeRequest2(
                `${GET_POWER_MONTHLY_USAGE_URL}/${facilityId}?year=${year}&month=${month}`,
                'GET',
                {}
            );

            if (response.success) {
                // NOTE: this endpoint currently only returns { meter_sn, usage } —
                // no customerName/unitName/previousReading/currentReading yet.
                // Defensive unwrap since the forwarder's nesting isn't confirmed.
                const raw =
                    response.data?.data?.usageData?.raw ||
                    response.data?.usageData?.raw ||
                    [];
                setMeterRows(
                    raw.map((r) => ({
                        meterNumber: String(r.meter_sn ?? 'Unknown'), // meter_sn comes back numeric for most rows
                        consumption: r.usage,
                        period: meterPeriodMonth,
                    }))
                );
                setMeterGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch meter consumption');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setMeterLoading(false);
        }
    };

    const filteredMeterRows = meterRows.filter((r) =>
        String(r.meterNumber || '').toLowerCase().includes(meterSearch.toLowerCase())
    );

    // ── Report 2: Money Collected Per Account ───────────────────────────
    const [moneyPeriod, setMoneyPeriod] = useState(emptyPeriod());
    const [moneySearch, setMoneySearch] = useState('');
    const [moneyLoading, setMoneyLoading] = useState(false);
    const [moneyGenerated, setMoneyGenerated] = useState(false);
    const [moneyRows, setMoneyRows] = useState([]);
    const [moneySummary, setMoneySummary] = useState({
        totalCollected: 0,
        totalAccounts: 0,
        totalTransactions: 0,
        avgPerAccount: 0,
    });

    const fetchMoneyCollected = async () => {
        try {
            setMoneyLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams({
                ...periodToQueryParams(moneyPeriod),
                ...(moneySearch ? { searchQuery: moneySearch } : {}),
            });

            const response = await makeRequest2(
                `${GET_POWER_MONEY_COLLECTED_REPORT_URL}/${facilityId}?${params.toString()}`,
                'GET',
                {}
            );

            if (response.success) {
                setMoneyRows(response.data.data.records || []);
                setMoneySummary(response.data.data.summary || {
                    totalCollected: 0, totalAccounts: 0, totalTransactions: 0, avgPerAccount: 0,
                });
                setMoneyGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch money collected report');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setMoneyLoading(false);
        }
    };

    // ── Report 3: Invoice Reports ────────────────────────────────────────
    const [invoiceMonth, setInvoiceMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
    const [invoiceStatus, setInvoiceStatus] = useState('All Status');
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [invoiceGenerated, setInvoiceGenerated] = useState(false);
    const [invoiceRows, setInvoiceRows] = useState([]);

    const fetchInvoices = async () => {
        try {
            setInvoiceLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams();
            if (invoiceMonth) params.append('yearMonth', invoiceMonth);
            if (invoiceStatus !== 'All Status') params.append('status', invoiceStatus);

            const response = await makeRequest2(
                `${GET_FACILITY_INVOICES_URL}/${facilityId}?${params.toString()}`,
                'GET',
                {}
            );

            if (response.success) {
                // Confirmed shape: { message, data: [...raw invoices], count, total, page, pages }
                // — a flat array of PowerInvoice docs, unitName already
                // enriched server-side by getFacilityInvoices.
                const rawInvoices = response.data?.data || [];

                setInvoiceRows(
                    rawInvoices.map((inv) => ({
                        invoiceNumber: inv.invoiceNumber,
                        meterNumber: inv.meterNumber,
                        customerName: inv.CustomerInfo?.fullName || inv.customerName || 'N/A',
                        unitName: inv.unitName || 'N/A',
                        totalAmount: inv.powerCharges?.totalCharge ?? inv.totalAmount ?? 0,
                        amountPaid: inv.amountPaid ?? 0,
                        balance: (inv.powerCharges?.totalCharge ?? inv.totalAmount ?? 0) - (inv.amountPaid ?? 0),
                        status: inv.status,
                        dateIssued: inv.dateIssued ? new Date(inv.dateIssued).toLocaleDateString() : 'N/A',
                        dueDate: inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : 'N/A',
                    }))
                );
                setInvoiceGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch invoices');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setInvoiceLoading(false);
        }
    };

    const invoiceTotals = {
        totalInvoices: invoiceRows.length,
        totalBilled: invoiceRows.reduce((s, r) => s + (r.totalAmount || 0), 0),
        totalPaid: invoiceRows.reduce((s, r) => s + (r.amountPaid || 0), 0),
        totalBalance: invoiceRows.reduce((s, r) => s + (r.balance || 0), 0),
    };

    // ── Reports 4 & 5: share one fetch — the dedicated facility trend
    //    endpoint aggregates PowerMeterMonthlyReading server-side for the
    //    last 12 months in one query. Facility Consumption shows it as a
    //    table, Trend shows it as a chart.
    const [trendLoading, setTrendLoading] = useState(false);
    const [trendGenerated, setTrendGenerated] = useState(false);
    const [trendData, setTrendData] = useState([]);
    const [allTime, setAllTime] = useState({ total: 0, firstMonth: null, lastMonth: null, meters: 0 });

    const yearMonthToLabel = (yearMonth) => {
        const [y, m] = yearMonth.split('-').map(Number);
        return `${MONTH_NAMES[m - 1]} ${y}`;
    };

    const fetchFacilityTrend = async () => {
        try {
            setTrendLoading(true);
            const facilityId = await getItem('selectedFacilityId');

            const response = await makeRequest2(
                `${GET_FACILITY_CONSUMPTION_TREND_URL}/${facilityId}?monthsBack=12`,
                'GET',
                {}
            );

            if (response.success) {
                const records = response.data?.data?.records || [];
                setTrendData(
                    records.map((r) => ({
                        month: yearMonthToLabel(r.yearMonth),
                        consumption: r.totalConsumption,
                        metersCount: r.metersCount,
                    }))
                );
                const summary = response.data?.data?.summary || {};
                setAllTime({
                    total: summary.allTimeConsumption || 0,
                    firstMonth: summary.allTimeFirstMonth,
                    lastMonth: summary.allTimeLastMonth,
                    meters: summary.allTimeMetersWithReadings || 0,
                });
                setTrendGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch facility consumption trend');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setTrendLoading(false);
        }
    };

    const trendTotal = trendData.reduce((s, r) => s + r.consumption, 0);
    const trendAvg = trendData.length ? Math.round(trendTotal / trendData.length) : 0;

    // ── Report 6: Facility Power Report (Residential/Commercial/Common Area) ──
    const [facilityPowerMonth, setFacilityPowerMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
    const [facilityPowerLoading, setFacilityPowerLoading] = useState(false);
    const [facilityPowerGenerated, setFacilityPowerGenerated] = useState(false);
    const [facilityPowerRows, setFacilityPowerRows] = useState([]);
    const [facilityPowerDetails, setFacilityPowerDetails] = useState([]);
    const [facilityPowerTotals, setFacilityPowerTotals] = useState({
        meterCount: 0,
        consumedPower: 0,
        cost: 0,
        collected: 0,
    });

    const fetchFacilityPowerReport = async () => {
        try {
            setFacilityPowerLoading(true);
            const facilityId = await getItem('selectedFacilityId');

            const response = await makeRequest2(
                `${GET_FACILITY_POWER_REPORT_URL}/${facilityId}?yearMonth=${facilityPowerMonth}`,
                'GET',
                {}
            );

            if (response.success) {
                const categories = response.data?.data?.categories || [];
                setFacilityPowerRows(
                    categories.map((c) => ({
                        label: c.label,
                        meterCount: c.meterCount,
                        consumedPower: c.consumedPower,
                        cost: c.cost,
                        collected: c.collected,
                        outstanding: (c.cost || 0) - (c.collected || 0),
                    }))
                );
                setFacilityPowerTotals(
                    response.data?.data?.totals || { meterCount: 0, consumedPower: 0, cost: 0, collected: 0 }
                );
                setFacilityPowerDetails(response.data?.data?.details || []);
                setFacilityPowerGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch facility power report');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setFacilityPowerLoading(false);
        }
    };


    // ── Northlands: Power transactions from M-Pesa ──────────────────────
    // Power accounts at Northlands start with 1 and have more than 10 digits (e.g. 14532339448).
    const [nlEnabled, setNlEnabled] = useState(false);
    const [nlStart, setNlStart] = useState('');
    const [nlEnd, setNlEnd] = useState('');
    const [nlSearch, setNlSearch] = useState('');
    const [nlStatus, setNlStatus] = useState('completed');
    const [nlLoading, setNlLoading] = useState(false);
    const [nlGenerated, setNlGenerated] = useState(false);
    const [nlRows, setNlRows] = useState([]);
    const [nlSummary, setNlSummary] = useState({ totalAmount: 0, totalTransactions: 0, totalAccounts: 0, avgPerTransaction: 0, truncated: false });

    useEffect(() => {
        (async () => {
            try {
                const facilityId = await getItem('selectedFacilityId');
                if (!facilityId) return;
                const res = await makeRequest2(`${NORTHLANDS_POWER_TRANSACTIONS_ENABLED_URL}/${facilityId}`, 'GET');
                setNlEnabled(!!res?.data?.data?.enabled);
            } catch {
                setNlEnabled(false);
            }
        })();
    }, []);

    const fetchNorthlandsPower = async () => {
        try {
            setNlLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams({
                ...(nlStart ? { startDate: nlStart } : {}),
                ...(nlEnd ? { endDate: nlEnd } : {}),
                ...(nlSearch.trim() ? { search: nlSearch.trim() } : {}),
                status: nlStatus,
            });
            const res = await makeRequest2(`${NORTHLANDS_POWER_TRANSACTIONS_URL}/${facilityId}?${params.toString()}`, 'GET', undefined, { timeout: 120000 });
            if (res?.success && res.data?.success) {
                setNlRows((res.data.data.records || []).map((r) => ({ ...r, date: r.createdAt ? new Date(r.createdAt).toLocaleString() : '' })));
                setNlSummary(res.data.data.summary);
                setNlGenerated(true);
                if (res.data.data.summary?.truncated) toastify('Too many transactions - narrow the date range to see them all.', 'warn');
            } else {
                throw new Error(res?.data?.error || res?.error || 'Failed to fetch power transactions');
            }
        } catch (error) {
            toastify(error.message, 'error');
        } finally {
            setNlLoading(false);
        }
    };

    return (
        <>
            <div className="row">
                <ReportLauncherCard
                    icon="ti-gauge"
                    title="Meter Consumption (Per Period)"
                    description="Every power meter's consumption for a chosen month."
                    onGenerate={() => setActiveModal('meterConsumption')}
                />
                <ReportLauncherCard
                    icon="ti-cash-banknote"
                    title="Money Collected Per Account"
                    description="How much was collected per power meter account over a chosen period."
                    onGenerate={() => setActiveModal('moneyCollected')}
                />
                <ReportLauncherCard
                    icon="ti-file-invoice"
                    title="Invoice Reports"
                    description="Invoices raised per meter — billed, paid, balance and status."
                    onGenerate={() => setActiveModal('invoices')}
                />
                <ReportLauncherCard
                    icon="ti-building"
                    title="Facility Consumption"
                    description="Whole-facility power consumption totals for the last 12 months."
                    onGenerate={() => setActiveModal('facility')}
                />
                <ReportLauncherCard
                    icon="ti-chart-bar"
                    title="Consumption Trend (12 Months)"
                    description="Facility-wide power consumption for each of the last 12 months, charted."
                    onGenerate={() => setActiveModal('trend')}
                />
                <ReportLauncherCard
                    icon="ti-bolt"
                    title="Facility Power Report"
                    description="Residential, Commercial and Common Area meters — consumption, cost and collected for a chosen month."
                    onGenerate={() => setActiveModal('facilityPower')}
                />
                {nlEnabled && (
                    <ReportLauncherCard
                        icon="ti-brand-cashapp"
                        title="Power Transactions (NCBA)"
                        description="Northlands power payments from the NCBA collection - accounts starting with 1 and more than 10 digits."
                        onGenerate={() => setActiveModal('northlandsPower')}
                    />
                )}
            </div>

            {/* ── Meter Consumption Per Period ── */}
            <ReportModal
                show={activeModal === 'meterConsumption'}
                onClose={closeModal}
                title="Power Meter Consumption (Per Period)"
                subtitle="Per-meter consumption for the selected month"
            >
                <div className="alert alert-info py-2 mb-3">
                    <i className="ti ti-info-circle me-2"></i>
                    <small>
                        This report currently shows meter # and consumption only — customer, unit and
                        previous/current readings need the monthly usage service extended to join
                        <code> PowerMeterAccount</code>.
                    </small>
                </div>

                <div className="row g-2 mb-3">
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Month</label>
                        <input type="month" className="form-control" value={meterPeriodMonth} onChange={(e) => setMeterPeriodMonth(e.target.value)} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Search Meter #</label>
                        <input className="form-control" placeholder="Meter #..." value={meterSearch} onChange={(e) => setMeterSearch(e.target.value)} />
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                        <button className="btn btn-primary w-100" onClick={fetchMeterConsumption} disabled={meterLoading}>
                            <i className="ti ti-refresh me-1"></i> {meterLoading ? 'Loading...' : 'Generate'}
                        </button>
                    </div>
                </div>

                {meterGenerated && (
                    <>
                        <ExportBar
                            disabled={filteredMeterRows.length === 0}
                            rows={filteredMeterRows}
                            columns={[
                                { field: 'meterNumber', header: 'Meter #' },
                                { field: 'consumption', header: 'Consumption (kWh)' },
                                { field: 'period', header: 'Period' },
                            ]}
                            filename={`power_meter_consumption_${meterPeriodMonth}`}
                            title={`Power Meter Consumption — ${meterPeriodMonth}`}
                        />
                        <DataTable value={filteredMeterRows} emptyMessage="No meters found for this month." sortMode="multiple" paginator rows={10} stripedRows tableStyle={{ minWidth: '30rem' }} loading={meterLoading}>
                            <Column field="meterNumber" sortable header="Meter #"></Column>
                            <Column field="consumption" sortable header="Consumption (kWh)"></Column>
                            <Column field="period" header="Period"></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

            {/* ── Money Collected Per Account ── */}
            <ReportModal
                show={activeModal === 'moneyCollected'}
                onClose={closeModal}
                title="Money Collected Per Power Account"
                subtitle="Collective and per-account totals for the selected period — prepaid top-ups + postpaid payments combined"
            >
                <div className="row g-2 mb-3">
                    <div className="col-md-9">
                        <PeriodFilter period={moneyPeriod} onChange={setMoneyPeriod} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Search</label>
                        <input className="form-control" placeholder="Account, customer, meter..." value={moneySearch} onChange={(e) => setMoneySearch(e.target.value)} />
                    </div>
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchMoneyCollected} disabled={moneyLoading}>
                        <i className="ti ti-refresh me-1"></i> {moneyLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {moneyGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="success" label="Total Collected" value={formatCurrency(moneySummary.totalCollected)} />
                            <SummaryCard bg="primary" label="Accounts" value={moneySummary.totalAccounts} />
                            <SummaryCard bg="info" label="Transactions" value={moneySummary.totalTransactions} />
                            <SummaryCard bg="warning" label="Avg / Account" value={formatCurrency(moneySummary.avgPerAccount)} />
                        </div>

                        <ExportBar
                            disabled={moneyRows.length === 0}
                            rows={moneyRows}
                            columns={[
                                { field: 'accountNumber', header: 'Account #' },
                                { field: 'customerName', header: 'Customer' },
                                { field: 'meterNumber', header: 'Meter #' },
                                { field: 'paymentType', header: 'Type' },
                                { field: 'transactionCount', header: '# Transactions' },
                                { field: 'totalCollected', header: 'Total Collected' },
                                { field: 'lastCollectionDate', header: 'Last Collection' },
                            ]}
                            filename={`power_money_collected_${moneyPeriod.month || 'report'}`}
                            title="Money Collected Per Power Account"
                        />

                        <DataTable value={moneyRows} emptyMessage="No collections found for this period." sortMode="multiple" paginator rows={10} stripedRows tableStyle={{ minWidth: '50rem' }} loading={moneyLoading}>
                            <Column field="accountNumber" sortable header="Account #"></Column>
                            <Column field="customerName" sortable header="Customer"></Column>
                            <Column field="meterNumber" header="Meter #"></Column>
                            <Column field="paymentType" header="Type" body={(r) => (
                                <span className={`badge ${r.paymentType === 'Postpaid' ? 'bg-primary text-light' : 'bg-info text-light'}`}>{r.paymentType}</span>
                            )}></Column>
                            <Column field="transactionCount" sortable header="# Transactions"></Column>
                            <Column field="totalCollected" sortable header="Total Collected" body={(r) => formatCurrency(r.totalCollected)}></Column>
                            <Column field="lastCollectionDate" header="Last Collection" body={(r) => r.lastCollectionDate ? new Date(r.lastCollectionDate).toLocaleDateString() : 'N/A'}></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

            {/* ── Invoice Reports ── */}
            <ReportModal
                show={activeModal === 'invoices'}
                onClose={closeModal}
                title="Power Invoice Reports"
                subtitle="Invoices raised per meter for the selected period"
            >
                <div className="row g-2 mb-3">
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Month</label>
                        <input type="month" className="form-control" value={invoiceMonth} onChange={(e) => setInvoiceMonth(e.target.value)} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Status</label>
                        <select className="form-control" value={invoiceStatus} onChange={(e) => setInvoiceStatus(e.target.value)}>
                            <option>All Status</option>
                            <option>Pending</option>
                            <option>Paid</option>
                            <option>Partially Paid</option>
                            <option>Overdue</option>
                            <option>Unpaid</option>
                            <option>Cancelled</option>
                        </select>
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                        <button className="btn btn-primary w-100" onClick={fetchInvoices} disabled={invoiceLoading}>
                            <i className="ti ti-refresh me-1"></i> {invoiceLoading ? 'Loading...' : 'Generate'}
                        </button>
                    </div>
                </div>

                {invoiceGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="primary" label="Total Invoices" value={invoiceTotals.totalInvoices} />
                            <SummaryCard bg="info" label="Total Billed" value={formatCurrency(invoiceTotals.totalBilled)} />
                            <SummaryCard bg="success" label="Total Paid" value={formatCurrency(invoiceTotals.totalPaid)} />
                            <SummaryCard bg="warning" label="Outstanding" value={formatCurrency(invoiceTotals.totalBalance)} />
                        </div>

                        <ExportBar
                            disabled={invoiceRows.length === 0}
                            rows={invoiceRows}
                            columns={[
                                { field: 'invoiceNumber', header: 'Invoice #' },
                                { field: 'meterNumber', header: 'Meter #' },
                                { field: 'customerName', header: 'Customer' },
                                { field: 'unitName', header: 'Unit' },
                                { field: 'totalAmount', header: 'Billed', accessor: (r) => formatCurrency(r.totalAmount) },
                                { field: 'amountPaid', header: 'Paid', accessor: (r) => formatCurrency(r.amountPaid) },
                                { field: 'balance', header: 'Balance', accessor: (r) => formatCurrency(r.balance) },
                                { field: 'status', header: 'Status' },
                                { field: 'dateIssued', header: 'Issued' },
                                { field: 'dueDate', header: 'Due' },
                            ]}
                            filename={`power_invoices_${invoiceMonth}`}
                            title={`Power Invoice Report — ${invoiceMonth}`}
                        />

                        <DataTable value={invoiceRows} emptyMessage="No invoices found." sortMode="multiple" paginator rows={10} stripedRows tableStyle={{ minWidth: '50rem' }} loading={invoiceLoading}>
                            <Column field="invoiceNumber" sortable header="Invoice #"></Column>
                            <Column field="meterNumber" header="Meter #"></Column>
                            <Column field="customerName" sortable header="Customer"></Column>
                            <Column field="unitName" header="Unit"></Column>
                            <Column field="totalAmount" sortable header="Billed" body={(r) => formatCurrency(r.totalAmount)}></Column>
                            <Column field="amountPaid" sortable header="Paid" body={(r) => formatCurrency(r.amountPaid)}></Column>
                            <Column field="balance" sortable header="Balance" body={(r) => formatCurrency(r.balance)}></Column>
                            <Column field="status" sortable header="Status" body={(r) => (
                                <span className={`badge ${
                                    r.status === 'Paid' ? 'bg-success text-light' :
                                    r.status === 'Unpaid' ? 'bg-danger text-light' :
                                    r.status === 'Partially Paid' ? 'bg-warning text-light' :
                                    'bg-danger text-light'
                                }`}>{r.status}</span>
                            )}></Column>
                            <Column field="dateIssued" header="Issued"></Column>
                            <Column field="dueDate" header="Due"></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

            {/* ── Facility-wide Consumption (table view of the 12-month data) ── */}
            <ReportModal
                show={activeModal === 'facility'}
                onClose={closeModal}
                title="Facility Power Consumption"
                subtitle="Whole-facility totals for the last 12 months"
            >
                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchFacilityTrend} disabled={trendLoading}>
                        <i className="ti ti-refresh me-1"></i> {trendLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {trendGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard
                                bg="danger"
                                label={allTime.firstMonth
                                    ? `Total To Date (${yearMonthToLabel(allTime.firstMonth)} – ${yearMonthToLabel(allTime.lastMonth)}, ${allTime.meters} meters)`
                                    : 'Total To Date (all meters)'}
                                value={`${allTime.total.toLocaleString()} kWh`}
                            />
                        </div>
                        <div className="row mb-3">
                            <SummaryCard bg="primary" label="Months Shown" value={trendData.length} />
                            <SummaryCard bg="info" label="12-Month Total" value={`${trendTotal.toLocaleString()} kWh`} />
                            <SummaryCard bg="success" label="Monthly Average" value={`${trendAvg.toLocaleString()} kWh`} />
                            <SummaryCard bg="warning" label="Meters (latest month)" value={trendData[trendData.length - 1]?.metersCount ?? 0} />
                        </div>

                        <ExportBar
                            disabled={trendData.length === 0}
                            rows={trendData}
                            columns={[
                                { field: 'month', header: 'Period' },
                                { field: 'metersCount', header: 'Meters' },
                                { field: 'consumption', header: 'Consumption (kWh)' },
                            ]}
                            filename={`power_facility_consumption`}
                            title="Facility Power Consumption"
                        />

                        <DataTable value={trendData} emptyMessage="No data found." sortMode="multiple" paginator rows={12} stripedRows tableStyle={{ minWidth: '30rem' }} loading={trendLoading}>
                            <Column field="month" sortable header="Period"></Column>
                            <Column field="metersCount" sortable header="Meters"></Column>
                            <Column field="consumption" sortable header="Consumption (kWh)" body={(r) => r.consumption.toLocaleString()}></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

            {/* ── Consumption Trend (12 Months) — chart view of the same data ── */}
            <ReportModal
                show={activeModal === 'trend'}
                onClose={closeModal}
                title="Power Consumption Trend"
                subtitle="Facility-wide consumption for the last 12 months"
            >
                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchFacilityTrend} disabled={trendLoading}>
                        <i className="ti ti-refresh me-1"></i> {trendLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {trendGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="primary" label="12-Month Total" value={`${trendTotal.toLocaleString()} kWh`} />
                            <SummaryCard bg="info" label="Monthly Average" value={`${trendAvg.toLocaleString()} kWh`} />
                            <SummaryCard bg="success" label="Peak Month" value={trendData.length ? trendData.reduce((a, b) => (b.consumption > a.consumption ? b : a)).month : 'N/A'} />
                            <SummaryCard bg="warning" label="Lowest Month" value={trendData.length ? trendData.reduce((a, b) => (b.consumption < a.consumption ? b : a)).month : 'N/A'} />
                        </div>

                        <div className="card">
                            <div className="card-body">
                                <ConsumptionTrendChart data={trendData.map(({ month, consumption }) => ({ month, consumption }))} unitLabel="kWh" barColor="#f0ad4e" />
                            </div>
                        </div>
                    </>
                )}
            </ReportModal>

            {/* ── Facility Power Report (Residential / Commercial / Common Area) ── */}
            <ReportModal
                show={activeModal === 'facilityPower'}
                onClose={closeModal}
                title="Facility Power Report"
                subtitle="Consumption, cost and collected — by meter category, for the selected month"
            >
                <div className="row g-2 mb-3">
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Month</label>
                        <input type="month" className="form-control" value={facilityPowerMonth} onChange={(e) => setFacilityPowerMonth(e.target.value)} />
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                        <button className="btn btn-primary w-100" onClick={fetchFacilityPowerReport} disabled={facilityPowerLoading}>
                            <i className="ti ti-refresh me-1"></i> {facilityPowerLoading ? 'Loading...' : 'Generate'}
                        </button>
                    </div>
                </div>

                {facilityPowerGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="primary" label="Total Meters" value={facilityPowerTotals.meterCount} />
                            <SummaryCard bg="info" label="Total Consumed" value={`${(facilityPowerTotals.consumedPower || 0).toLocaleString()} kWh`} />
                            <SummaryCard bg="warning" label="Total Cost" value={formatCurrency(facilityPowerTotals.cost)} />
                            <SummaryCard bg="success" label="Total Collected" value={formatCurrency(facilityPowerTotals.collected)} />
                        </div>

                        <ExportBar
                            disabled={facilityPowerRows.length === 0}
                            rows={facilityPowerRows}
                            columns={[
                                { field: 'label', header: 'Category' },
                                { field: 'meterCount', header: 'Meters' },
                                { field: 'consumedPower', header: 'Consumed (kWh)' },
                                { field: 'cost', header: 'Cost', accessor: (r) => formatCurrency(r.cost) },
                                { field: 'collected', header: 'Collected', accessor: (r) => formatCurrency(r.collected) },
                                { field: 'outstanding', header: 'Outstanding', accessor: (r) => formatCurrency(r.outstanding) },
                            ]}
                            filename={`facility_power_report_${facilityPowerMonth}`}
                            title={`Facility Power Report — ${facilityPowerMonth}`}
                        />

                        <DataTable value={facilityPowerRows} emptyMessage="No meters found for this facility." sortMode="multiple" stripedRows tableStyle={{ minWidth: '40rem' }} loading={facilityPowerLoading}>
                            <Column field="label" header="Category"></Column>
                            <Column field="meterCount" sortable header="Meters"></Column>
                            <Column field="consumedPower" sortable header="Consumed (kWh)" body={(r) => r.consumedPower.toLocaleString()}></Column>
                            <Column field="cost" sortable header="Cost" body={(r) => formatCurrency(r.cost)}></Column>
                            <Column field="collected" sortable header="Collected" body={(r) => formatCurrency(r.collected)}></Column>
                            <Column field="outstanding" sortable header="Outstanding" body={(r) => formatCurrency(r.outstanding)}></Column>
                        </DataTable>

                        <h6 className="mt-4 mb-1">Source data (per meter)</h6>
                        <p className="text-muted small mb-2">
                            Every figure above is the sum of these rows. Cost = prepaid debits or postpaid invoices for the month;
                            Note explains meters with consumption but no cost.
                        </p>
                        <ExportBar
                            disabled={facilityPowerDetails.length === 0}
                            rows={facilityPowerDetails}
                            columns={[
                                { field: 'category', header: 'Category' },
                                { field: 'meterSerialNumber', header: 'Meter' },
                                { field: 'description', header: 'Description' },
                                { field: 'accountNumber', header: 'Account' },
                                { field: 'paymentType', header: 'Payment Type' },
                                { field: 'consumedKwh', header: 'Consumed (kWh)' },
                                { field: 'costSource', header: 'Cost Source' },
                                { field: 'costRecords', header: 'Cost Records' },
                                { field: 'cost', header: 'Cost' },
                                { field: 'effectiveRate', header: 'Cost per kWh' },
                                { field: 'collectedSource', header: 'Collected Source' },
                                { field: 'collected', header: 'Collected' },
                                { field: 'note', header: 'Note' },
                            ]}
                            filename={`facility_power_report_details_${facilityPowerMonth}`}
                            title={`Facility Power Report Details — ${facilityPowerMonth}`}
                        />
                        <DataTable value={facilityPowerDetails} paginator rows={20} sortMode="multiple" stripedRows size="small" tableStyle={{ minWidth: '60rem' }} emptyMessage="No meters.">
                            <Column field="category" sortable header="Category"></Column>
                            <Column field="meterSerialNumber" sortable header="Meter"></Column>
                            <Column field="paymentType" sortable header="Type"></Column>
                            <Column field="consumedKwh" sortable header="kWh"></Column>
                            <Column field="cost" sortable header="Cost" body={(r) => formatCurrency(r.cost)}></Column>
                            <Column field="effectiveRate" sortable header="Cost/kWh"></Column>
                            <Column field="collected" sortable header="Collected" body={(r) => formatCurrency(r.collected)}></Column>
                            <Column field="note" header="Note"></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>
            {/* ── Northlands: Power transactions from M-Pesa ── */}
            <ReportModal
                show={activeModal === 'northlandsPower'}
                onClose={closeModal}
                title="Power Transactions (NCBA)"
                subtitle="Northlands power payments from the NCBA collection - accounts start with 1, more than 10 digits"
            >
                <div className="row g-2 mb-3 align-items-end">
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Start Date</label>
                        <input type="date" className="form-control" value={nlStart} onChange={(e) => setNlStart(e.target.value)} />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">End Date</label>
                        <input type="date" className="form-control" value={nlEnd} onChange={(e) => setNlEnd(e.target.value)} />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Status</label>
                        <select className="form-control" value={nlStatus} onChange={(e) => setNlStatus(e.target.value)}>
                            <option value="completed">Completed</option>
                            <option value="pending">Pending</option>
                            <option value="failed">Failed</option>
                            <option value="all">All</option>
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Search</label>
                        <input className="form-control" value={nlSearch} onChange={(e) => setNlSearch(e.target.value)} placeholder="Account, ref, name" />
                    </div>
                    <div className="col-md-2">
                        <button className="btn btn-primary w-100" onClick={fetchNorthlandsPower} disabled={nlLoading}>
                            <i className="ti ti-refresh me-1"></i> {nlLoading ? 'Loading...' : 'Generate'}
                        </button>
                    </div>
                </div>

                {nlGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="success" label="Total Received" value={formatCurrency(nlSummary.totalAmount)} />
                            <SummaryCard bg="primary" label="Transactions" value={nlSummary.totalTransactions} />
                            <SummaryCard bg="info" label="Power Accounts" value={nlSummary.totalAccounts} />
                            <SummaryCard bg="warning" label="Average / Transaction" value={formatCurrency(nlSummary.avgPerTransaction)} />
                        </div>

                        <ExportBar
                            disabled={nlRows.length === 0}
                            rows={nlRows}
                            columns={[
                                { field: 'date', header: 'Date' },
                                { field: 'mpesaReceiptNumber', header: 'Transaction ID' },
                                { field: 'accountNumber', header: 'Account' },
                                { field: 'customerName', header: 'Customer' },
                                { field: 'mobile', header: 'Phone' },
                                { field: 'amount', header: 'Amount', accessor: (r) => formatCurrency(r.amount) },
                                { field: 'status', header: 'Status' },
                                { field: 'error', header: 'Billing Error' },
                            ]}
                            filename={`northlands_power_transactions_${nlStart || 'all'}_${nlEnd || 'all'}`}
                            title="Northlands Power Transactions (NCBA)"
                        />

                        <DataTable value={nlRows} emptyMessage="No power transactions found." sortMode="multiple" paginator rows={15} stripedRows size="small" tableStyle={{ minWidth: '50rem' }} loading={nlLoading}>
                            <Column field="date" sortable header="Date"></Column>
                            <Column field="mpesaReceiptNumber" sortable header="Transaction ID"></Column>
                            <Column field="accountNumber" sortable header="Account"></Column>
                            <Column field="customerName" sortable header="Customer" body={(r) => r.customerName || '-'}></Column>
                            <Column field="mobile" header="Phone" body={(r) => r.mobile || '-'}></Column>
                            <Column field="amount" sortable header="Amount" body={(r) => formatCurrency(r.amount)}></Column>
                            <Column field="status" sortable header="Status"></Column>
                            <Column field="error" header="Billing Error" body={(r) => r.error || '-'}></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>
        </>
    );
}

export default PowerReportsTab;